import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  TooltipProvider,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@bpinternal/ui-kit-next'
import { ClipboardCopyIcon, DownloadIcon, EllipsisIcon, LinkIcon, Maximize2 } from 'lucide-react'
import React, {
  forwardRef,
  memo,
  useCallback,
  useMemo,
  useState,
  type ButtonHTMLAttributes,
  type FC,
  type HTMLAttributes,
  type ReactNode,
} from 'react'
import { cn } from '~/utils'

interface ImageActionsProps {
  shouldMerge?: boolean
  isLink?: boolean
  onView?: () => void
  onDownload?: () => void
  onCopy?: () => void
  onCopyLink?: () => void
}

interface ActionButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: ReactNode
  tooltip: string
}

export const ActionWrapper = memo(
  forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'absolute right-3 top-3 flex flex-row rounded px-0.5 opacity-0 group-hover/node-image:opacity-100',
        'border-[0.5px] bg-[var(--mt-bg-secondary)] [backdrop-filter:saturate(1.8)_blur(20px)]',
        className
      )}
      {...props}
    >
      {children}
    </div>
  ))
)

ActionWrapper.displayName = 'ActionWrapper'

export const ActionButton = memo(
  forwardRef<HTMLButtonElement, ActionButtonProps>(({ icon, tooltip, className, ...props }, ref) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={ref}
            variant="ghost"
            className={cn(
              'relative flex h-7 w-7 flex-row rounded-none p-0 text-muted-foreground hover:text-foreground',
              'bg-transparent hover:bg-transparent',
              className
            )}
            {...props}
          >
            {icon}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ))
)

ActionButton.displayName = 'ActionButton'

type ActionKey = 'onView' | 'onDownload' | 'onCopy' | 'onCopyLink'

const ActionItems: Array<{
  key: ActionKey
  icon: ReactNode
  tooltip: string
  isLink?: boolean
}> = [
  {
    key: 'onView',
    icon: <Maximize2 className="size-4" />,
    tooltip: 'View image',
  },
  {
    key: 'onDownload',
    icon: <DownloadIcon className="size-4" />,
    tooltip: 'Download image',
  },
  {
    key: 'onCopy',
    icon: <ClipboardCopyIcon className="size-4" />,
    tooltip: 'Copy image to clipboard',
  },
  {
    key: 'onCopyLink',
    icon: <LinkIcon className="size-4" />,
    tooltip: 'Copy image link',
    isLink: true,
  },
]

export const ImageActions: FC<ImageActionsProps> = memo(({ shouldMerge = false, isLink = false, ...actions }) => {
  const [isOpen, setIsOpen] = useState(false)

  const handleAction = useCallback((e: MouseEvent, action: (() => void) | undefined) => {
    e.preventDefault()
    e.stopPropagation()
    action?.()
  }, [])

  const filteredActions = useMemo(() => ActionItems.filter((item) => isLink || !item.isLink), [isLink])

  return (
    <ActionWrapper className={cn({ 'opacity-100': isOpen })}>
      {shouldMerge ? (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger asChild>
            <ActionButton
              icon={<EllipsisIcon className="size-4" />}
              tooltip="Open menu"
              onClick={(e) => e.preventDefault()}
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end">
            {filteredActions.map(({ key, icon, tooltip }) => (
              <DropdownMenuItem key={key} onClick={(e) => handleAction(e as unknown as MouseEvent, actions[key])}>
                <div className="flex flex-row items-center gap-2">
                  {icon}
                  <span>{tooltip}</span>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        filteredActions.map(({ key, icon, tooltip }) => (
          <ActionButton
            key={key}
            icon={icon}
            tooltip={tooltip}
            onClick={(e) => handleAction(e as unknown as MouseEvent, actions[key])}
          />
        ))
      )}
    </ActionWrapper>
  )
})

ImageActions.displayName = 'ImageActions'
